/** Amber banner shown to unverified accounts — inline 6-digit code entry. */
import { useState } from "react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import { MailWarning, Loader2 } from "lucide-react";

export const VerifyEmailBanner = () => {
  const { user, refresh } = useAuth();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  if (!user || user.email_verified) return null;

  const verify = async () => {
    setBusy(true);
    try {
      await api.post("/auth/verify-email", { code: code.trim() });
      await refresh();
      toast.success("Email verified — full access unlocked");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verification failed");
    } finally { setBusy(false); }
  };

  const resend = async () => {
    try { await api.post("/auth/resend-verification"); toast.success("Verification email re-sent"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not resend"); }
  };

  return (
    <div className="bg-amber-50 border-b-2 border-amber-300 px-4 py-2.5" data-testid="verify-email-banner">
      <div className="max-w-7xl mx-auto flex flex-wrap items-center gap-3 text-sm">
        <span className="inline-flex items-center gap-2 font-bold text-amber-900">
          <MailWarning size={16}/>Verify your email to create inspections
        </span>
        <div className="flex items-center gap-2 ml-auto">
          <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            inputMode="numeric" placeholder="6-digit code" data-testid="verify-banner-code"
            className="h-9 w-32 border-2 border-amber-300 bg-white px-2 text-center font-black tracking-widest text-slate-900 focus:border-amber-600 focus:outline-none"/>
          <button onClick={verify} disabled={busy || code.length !== 6} data-testid="verify-banner-submit"
            className="h-9 px-3 bg-amber-500 text-slate-900 font-black text-xs hover:bg-amber-400 disabled:opacity-60 inline-flex items-center gap-1">
            {busy ? <Loader2 size={12} className="animate-spin"/> : null}Verify
          </button>
          <button onClick={resend} data-testid="verify-banner-resend"
            className="text-xs font-bold text-amber-800 underline">Resend</button>
        </div>
      </div>
    </div>
  );
};
